import posthog from 'posthog-js';
import { env } from './env';
import { splitFullName } from './string';

/**
 * PostHog event name catalog. String values must match webui's
 * `AnalyticsEvents` enum exactly — existing PostHog dashboards/insights key
 * off these strings, so any drift silently breaks them.
 */
export const AnalyticsEvent = {
  SignedIn: 'Signed In',
  SignedOut: 'Signed Out',
  VerificationDone: 'Verification done',
  NameAdded: 'Name added',
  PhoneNumberCaptured: 'Phone Number captured',
  IndustryOrBusinessDomainCaptured: 'Industry/business domain captured',
  CompanySizeCaptured: 'Company Size captured',
  SendEmailsPerMonthCaptured: 'Send Emails per month captured',
  MailboxesToWarmupCaptured: 'Mailboxes to Warmup captured',
  OutreachPlatformCaptured: 'Outreach Platform captured',
  DemoMeetingBooked: 'demo meeting booked',
  TrialBannerInteracted: 'trial_banner_interacted',
  ReportsImageDownloaded: 'Reports Image Downloaded',
  // Cancellation retention flow — webui's `CANCELLATION_FLOW_EVENTS`.
  CancellationFlowStarted: 'cancellation_flow_started',
  CancellationFlowRetained: 'cancellation_flow_retained',
  CancellationFlowProceeded: 'cancellation_flow_proceeded',
  CancellationFlowCancelled: 'cancellation_flow_cancelled',
  // Email account detail → Overview tab (v2 only; no webui counterpart).
  MailboxOverviewViewed: 'mailbox_overview_viewed',
  MailboxOverviewInteracted: 'mailbox_overview_interacted',
  // HighAdvocacy review-rewards popup (v2 only; no webui counterpart).
  ReviewRewardPopupShown: 'High Advocacy Review Reward Popup Shown',
} as const;

export type AnalyticsEvent = (typeof AnalyticsEvent)[keyof typeof AnalyticsEvent];

/**
 * Stamped as `version` on every event this app sends — custom events and
 * PostHog's own (`$pageview`, `$autocapture`, `$exception`, `$identify`, …).
 * webui reports into the same project under the same event names, so this
 * property is the only way to tell v2 traffic apart; webui events don't carry it.
 *
 * Added in `before_send` rather than `posthog.register()` on purpose: a
 * registered super property is persisted to localStorage, so if v1 and v2 ever
 * share an origin (cutover, rollback) webui's events would inherit the v2 tag.
 */
export const ANALYTICS_VERSION = 'v2';

/** Set by the marketing site on the parent domain; webui sent it as `$referrer`. */
const REFERRER_COOKIE = 'shreferer';

const MARKETING_UTM_PARAMS = [
  'utm_source',
  'utm_medium',
  'utm_campaign',
  'utm_term',
  'utm_content',
] as const;

/**
 * The marketing site's PostHog ids, when its signup link hands them over
 * (`distinct_id` + `session_id`, in the query or the `#hash` — webui's
 * `readPosthogBootstrapIdsFromUrl`). Bootstrapping with them keeps the visitor
 * the SAME PostHog person across trulyinbox.com → the app, so the person's
 * `$initial_referrer` stays the real external source (Google, LinkedIn…)
 * rather than the marketing site. Both are required: one alone is ignored.
 */
function readBootstrapIdsFromUrl(): { distinctID: string; sessionID: string } | undefined {
  const search = new URLSearchParams(window.location.search);
  const hash = new URLSearchParams(window.location.hash.slice(1));
  const distinctId = search.get('distinct_id') || hash.get('distinct_id');
  const sessionId = search.get('session_id') || hash.get('session_id');
  return distinctId && sessionId ? { distinctID: distinctId, sessionID: sessionId } : undefined;
}

/**
 * Initializes PostHog once, before the app renders. No-ops when no token is
 * configured so local dev without `VITE_POSTHOG_KEY` doesn't error or hit
 * the network.
 */
export function initAnalytics(): void {
  if (!env.posthogKey) return;
  posthog.init(env.posthogKey, {
    api_host: env.posthogHost,
    autocapture: true,
    capture_exceptions: true,
    persistence: 'localStorage+cookie',
    bootstrap: readBootstrapIdsFromUrl(),
    before_send: (event) => {
      if (event) event.properties = { ...event.properties, version: ANALYTICS_VERSION };
      return event;
    },
  });
  registerMarketingAttribution();
}

/**
 * The marketing site forwards `?ref=<page>&utm_*=…` on its signup links. Same
 * property names as webui so attribution insights span both apps. Only runs
 * when `ref` is present: a later visit without it must not overwrite the
 * registered values with nulls.
 */
function registerMarketingAttribution(): void {
  const params = new URLSearchParams(window.location.search);
  const ref = params.get('ref');
  if (!ref) return;
  posthog.register({
    $marketing_ref: ref,
    ...Object.fromEntries(
      MARKETING_UTM_PARAMS.map((param) => [`marketing_${param}`, params.get(param)]),
    ),
  });
  posthog.setPersonProperties(undefined, { $initial_marketing_ref: ref });
}

/**
 * Captures a PostHog event. Analytics must never throw into calling code —
 * failures are swallowed (and logged) rather than propagated.
 */
export function trackEvent(event: AnalyticsEvent, properties?: Record<string, unknown>): void {
  try {
    posthog.capture(event, properties);
  } catch (error) {
    console.error('[analytics] Failed to capture event:', error);
  }
}

/**
 * The URL at the moment of capture (webui's `getPosthogPageSnapshotAtCapture`).
 * Spread into events fired right before a navigation — PostHog's own
 * `$current_url` can already reflect the next route by the time it's read.
 */
export function pageSnapshotProperties(): { $current_url: string; signup_step_path: string } {
  return {
    $current_url: window.location.href,
    signup_step_path: `${window.location.pathname}${window.location.search}`,
  };
}

/**
 * `Signed In`, with webui's property keys. Not for OAuth / Google One Tap /
 * Saleshandy logins — the api sends those (it knows the provider).
 */
export function trackSignedIn(email: string, source: string): void {
  trackEvent(AnalyticsEvent.SignedIn, { 'User Email': email, source });
}

/**
 * Raw (not URI-decoded) cookie value, as webui's `getCookie` returned it — so
 * `$referrer` values from both apps group together in PostHog.
 */
function readCookie(name: string): string | null {
  const prefix = `${name}=`;
  const entry = document.cookie.split('; ').find((part) => part.startsWith(prefix));
  return entry ? entry.slice(prefix.length) || null : null;
}

/**
 * Identifies the current user to PostHog. Called from the auth chokepoints
 * (`storeAuth`) so every login path identifies consistently. Person properties
 * mirror webui's identify (`$email`/`$name` drive PostHog's person display).
 */
export function identifyUser(user: { id: number; email: string; name: string | null }): void {
  try {
    const name = user.name?.trim() ?? '';
    const { firstName, lastName } = splitFullName(name);
    const referrer = readCookie(REFERRER_COOKIE);
    posthog.identify(String(user.id), {
      email: user.email,
      $email: user.email,
      ...(name ? { name, $name: name } : {}),
      ...(firstName ? { 'First Name': firstName } : {}),
      ...(lastName ? { 'Last Name': lastName } : {}),
      ...(referrer ? { $referrer: referrer } : {}),
    });
  } catch (error) {
    console.error('[analytics] Failed to identify user:', error);
  }
}

/**
 * Resets PostHog identity on sign-out so the next session starts anonymous.
 */
export function resetAnalytics(): void {
  try {
    posthog.reset();
  } catch (error) {
    console.error('[analytics] Failed to reset:', error);
  }
}
